import React, { useState } from "react";
import {
  Clock,
  ChevronLeft,
  Camera,
  Play,
  RefreshCw,
  ChevronDown,
  Video,
} from "lucide-react";

const TimeSelectionStep = ({
  selectedCamera,
  loadingCameras,
  startTime,
  endTime,
  error,
  isGenerating,
  conversionStatus,
  onBack,
  onStartTimeChange,
  onEndTimeChange,
  onTimePreset,
  onTimeSelection,
}) => {
  const [showQuickTimeDropdown, setShowQuickTimeDropdown] = useState(false);
  const [selectedQuickTime, setSelectedQuickTime] = useState(null);

  const quickTimeOptions = [
    { label: "Last 5 minutes", minutes: 5 },
    { label: "Last 10 minutes", minutes: 10 },
    { label: "Last 15 minutes", minutes: 15 },
    { label: "Last 30 minutes", minutes: 30 },
    { label: "Last 2 hours", minutes: 120 },
  ];

  const handleQuickTimeSelect = (option) => {
    setSelectedQuickTime(option);
    setShowQuickTimeDropdown(false);
    onTimePreset(option.minutes);
  };

  return (
    <div className="bg-white rounded-2xl shadow-xl p-6 border border-gray-100">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-2">
          <div className="p-2 bg-green-100 rounded-lg">
            <Clock className="h-6 w-6 text-green-600" />
          </div>
          <h2 className="text-2xl font-semibold text-gray-900">
            Select Time Range
          </h2>
        </div>
        <button
          onClick={onBack}
          className="flex items-center space-x-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
        >
          <ChevronLeft className="h-4 w-4" />
          <span>Back</span>
        </button>
      </div>

      {/* Selected Camera Info - Hide IP Address */}
      {loadingCameras ? (
        <div className="bg-blue-50 rounded-xl p-4 mb-6 border border-blue-200">
          <div className="flex items-center space-x-3">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-400"></div>
            <div>
              <h3 className="font-semibold text-blue-900">
                Loading camera details...
              </h3>
              <p className="text-sm text-blue-700">Please wait while we fetch camera information</p>
            </div>
          </div>
        </div>
      ) : selectedCamera ? (
        <div className="bg-indigo-50 rounded-xl p-4 mb-6 border border-indigo-200">
          <div className="flex items-center space-x-3">
            <Camera className="h-5 w-5 text-indigo-600" />
            <div>
              <h3 className="font-semibold text-indigo-900">
                {selectedCamera.name}
              </h3>
              {/* IP address removed as requested */}
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-red-50 rounded-xl p-4 mb-6 border border-red-200">
          <div className="flex items-center space-x-3">
            <Camera className="h-5 w-5 text-red-600" />
            <div>
              <h3 className="font-semibold text-red-900">
                No camera selected
              </h3>
              <p className="text-sm text-red-700">Please select a camera to continue</p>
            </div>
          </div>
        </div>
      )}

      {error && !loadingCameras && !selectedCamera && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl">
          <div className="flex items-center space-x-2">
            <div className="h-5 w-5 text-red-500">⚠️</div>
            <span className="text-red-700 text-sm font-medium">{error}</span>
          </div>
        </div>
      )}

      <div className="space-y-6">
        {/* Quick Time Selection - Converted to Dropdown */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <label className="block text-sm font-medium text-gray-700">
              Quick Time Selection
            </label>
            {/* IST label removed as requested */}
          </div>
          
          {/* Dropdown for Quick Time Selection */}
          <div className="relative">
            <button
              onClick={() => setShowQuickTimeDropdown(!showQuickTimeDropdown)}
              className="w-full flex items-center justify-between px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white"
            >
              <span className="text-gray-700">
                {selectedQuickTime ? selectedQuickTime.label : "Select time range..."}
              </span>
              <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${showQuickTimeDropdown ? 'rotate-180' : ''}`} />
            </button>
            
            {showQuickTimeDropdown && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-xl shadow-lg">
                {quickTimeOptions.map((option) => (
                  <button
                    key={option.minutes}
                    onClick={() => handleQuickTimeSelect(option)}
                    className="w-full px-4 py-3 text-left hover:bg-gray-50 first:rounded-t-xl last:rounded-b-xl transition-colors"
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Custom Time Range */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <label className="block text-sm font-medium text-gray-700">
              Custom Time Range
            </label>
            {/* IST label removed as requested */}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-600 mb-2">
                Start Time
              </label>
              <input
                type="datetime-local"
                value={startTime}
                onChange={(e) => onStartTimeChange(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>

            <div>
              <label className="block text-sm text-gray-600 mb-2">
                End Time
              </label>
              <input
                type="datetime-local"
                value={endTime}
                onChange={(e) => onEndTimeChange(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
          </div>
        </div>

        {/* Video Preview Area - Changed from Generate Video Button */}
        <div className="space-y-4">
          {/* Video Preview Thumbnail */}
          <div className="bg-gray-100 rounded-xl p-6 border-2 border-dashed border-gray-300 min-h-[200px] flex items-center justify-center">
            <div className="text-center">
              <Video className="h-12 w-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-500 text-sm">Video preview will appear here</p>
              <p className="text-gray-400 text-xs mt-1">Select time range to generate preview</p>
            </div>
          </div>

          {/* Play Back Button */}
          <button
            onClick={onTimeSelection}
            disabled={loadingCameras || !selectedCamera || !startTime || !endTime || isGenerating}
            className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 disabled:from-gray-400 disabled:to-gray-500 text-white px-6 py-4 rounded-xl font-medium transition-all duration-200 flex items-center justify-center space-x-2 shadow-lg hover:shadow-xl disabled:shadow-none"
          >
            {isGenerating ? (
              <>
                <RefreshCw className="h-5 w-5 animate-spin" />
                <span>{conversionStatus}</span>
              </>
            ) : (
              <>
                <Play className="h-5 w-5" />
                <span>Play Back</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TimeSelectionStep;
